import { Component, OnInit, Input, Output, EventEmitter, ViewChild, AfterViewInit, OnDestroy} from '@angular/core';
import { timer } from 'rxjs';

import { AppState, ShowRequest } from '../../../core';
import { ScoutPoint, ScoutPointRefData, ScoutPointEnvData, Point } from '../../../core/models';
import { NgForm } from '@angular/forms';
import { FesBaseComponent } from '../../../shared/components/fes-base.component';
import { skip } from 'rxjs/operators';
import LatLonEllipsoidal_Vincenty from 'geodesy/latlon-ellipsoidal-vincenty';
import { ScoutTaskService } from '../../../core/services';
import { PointDoc } from '../../../core/schema/point-schema';

@Component({
  selector: 'app-scout-point-editor',
  templateUrl: './scout-point-editor.component.html',
  styleUrls: ['./scout-point-editor.component.scss']
})
export class ScoutPointEditorComponent extends FesBaseComponent implements OnInit, AfterViewInit, OnDestroy {

  static HA_BUILDING_RE : RegExp = new RegExp('^[A-Z][A-Z0-9]+$')

  @Input() scoutPoint : ScoutPoint
  @Input() set buildingPoints( pts :  PointDoc[] ) {
    if ( !pts ) {
      this.haBuildingPoints = [ Point.NewPoint ]
    } else {
      this.haBuildingPoints = [ Point.NewPoint, ...pts.filter(point=>ScoutPointEditorComponent.HA_BUILDING_RE.test(point._id)) ]
    }
  }
  @Input() bearingDefaultAngles : number[]
  @Input() scoutPoints : ScoutPoint[] = []
  @Input() isLoadingBuildingPoints : boolean = false
  @Input() isEditing : boolean = false
  @Input() showTitlebar : boolean = true
  @Input() showErrorCalcFields = false
  @Input() locked : boolean
  @Output() selected = new EventEmitter<ScoutPoint>()
  @Output() changed = new EventEmitter<ScoutPoint>()
  @Output() formStatusChanged = new EventEmitter<[ScoutPoint,string]>()
  @Output() editModeChanged = new EventEmitter<ScoutPointEditorComponent>()

  @ViewChild('scoutPointForm') scoutPointForm: NgForm;

  readonly NewPointId : string = Point.NewPointId
  readonly NewPoint : Point = Point.NewPoint

  haBuildingPoints : Point[] = [ Point.NewPoint ]
  scoutFromOptions : ScoutPoint[] = null
  exNameSuggestions : string[] = []

  constructor(appState : AppState, protected scoutTaskService : ScoutTaskService) {
    super(appState)
  }


  ngOnInit(): void {
    this.setScoutPoint(this.scoutPoint)
  }


  ngAfterViewInit(): void {
    this.subscriptions.push( 
      this.scoutPointForm.statusChanges.pipe(skip(0)).subscribe( 
        status => timer(0).subscribe(ms=> this.scoutPoint && this.formStatusChanged.emit([this.scoutPoint,status]))
      )
    )
    this.subscriptions.push( 
      this.scoutPointForm.valueChanges.pipe(skip(3)).subscribe(
        val => {
          // uncomment this block if we need to display up to date bearing for HA points before saving
          //if ( this.scoutPoint?.type == 'HA') {
          //  const parentPt = this.scoutPoints.find(pt=>pt.id==this.scoutPoint.parentId)
          //  this.scoutTaskService.updateDerivedHaPointProperties(this.scoutPoint,parentPt)
          //}
          this.scoutPointForm.valid && timer(0).subscribe(ms=> this.scoutPoint && this.changed.emit(this.scoutPoint))
        }
      )
    )
  }


  ngOnDestroy() {
    this.destroy()
  }

  edit(bEdit:boolean=true) {
    if ( this.isEditing != bEdit ) {
      this.isEditing = bEdit
      this.editModeChanged.emit(this)
    }
  }

  cancelEdit() {
    this.edit(false)
  }

  onPointIdChanged() {
    console.log('Point DB id changed to ' + this.scoutPoint.pointId)
    this.scoutPointForm.form.markAsDirty()
    this.changed.emit(this.scoutPoint)
  }


  onTypeChanged(newType:string) {
    const oldType = this.scoutPoint.type
    this.scoutPoint.type = newType
    console.log('Point type changed from ' + oldType + ' to ' + this.scoutPoint.type)
    if ( newType == 'HA' ) {      
      this.scoutPoint.pointId = Point.NewPointId
      this.scoutPoint.horizontalError.derivedFromExternalWall = false
      this.scoutPoint.verticalError.unlevelFloor = false
    } else if ( oldType == 'HA' && this.scoutPoint.parentId ) {
      // convert lat/lng to path bearing and horiz distance
      const lat = Number(this.scoutPoint.latitude)
      const lng = Number(this.scoutPoint.longitude)
      const parentPt = this.scoutPoints.find(pt=>pt.id==this.scoutPoint.parentId)
      if ( parentPt ) {
        const parentLat = Number(parentPt.latitude)
        const parentLng = Number(parentPt.longitude)
        if ( !isNaN(lat) && !isNaN(lng) ) {
          const parentVincenty = new LatLonEllipsoidal_Vincenty(parentLat, parentLng)
          const ptVincenty = new LatLonEllipsoidal_Vincenty(lat, lng)
          const bearing = parentVincenty.finalBearingTo(ptVincenty)
          const distance = parentVincenty.distanceTo(ptVincenty)
          this.scoutPoint.bearing = bearing
          this.scoutPoint.horizontalDistance = distance
        }
      }
    }
    this.scoutPointForm.form.markAsDirty()
    this.changed.emit(this.scoutPoint)
  }


  onFromChanged() {
    console.log('Point parent changed to ' + this.scoutPoint.parentId)
    this.scoutPointForm.form.markAsDirty()
    this.changed.emit(this.scoutPoint)
  }


  onShowPoint(pointId : string) {
    this.appState.showView.next( new ShowRequest('tab','point', pointId) )
  }

 
  onValueClicked( fieldName: string ) {
    this.isEditing = true
    this.editModeChanged.emit(this)
  }
  

  onBearingSelected( angle : number ) {
    try {
      if ( Number(this.scoutPoint.bearing) != Number(angle) ) {
        this.scoutPointForm.form.markAsDirty()
      }
    } catch ( error ) {    
      this.scoutPointForm.form.markAsDirty()
    }
    this.scoutPoint.bearing=angle;
  }


  rebuildPointNameSuggestions() {
    this.exNameSuggestions = []
    const startPt = this.scoutTaskService.getScoutPointsStart(this.scoutPoints)
    let baseNameSuggestion = startPt.pointId==this.NewPointId ? startPt.exPointId : startPt.pointId
    if ( !baseNameSuggestion ) {
      return
    }
    baseNameSuggestion += 'EX-'
    const baseNameLength = baseNameSuggestion.length
    this.exNameSuggestions.push(baseNameSuggestion)
    const usedExIdNumbers = this.scoutPoints.filter(pt=>pt.exPointId?.startsWith(baseNameSuggestion))
      .map(pt=>Number(pt.exPointId.substring(baseNameLength))).filter(idNumber=>!Number.isNaN(idNumber)).sort()
    console.log( usedExIdNumbers )
    let maxIdNumber = usedExIdNumbers.length ? usedExIdNumbers[usedExIdNumbers.length-1] : 0
    this.exNameSuggestions.push(baseNameSuggestion + (maxIdNumber + 1))
    //TODO - suggest 3 or 4 more values based on gaps in existing list
  }


  onDerivedFromExternalWallClick() {
    this.scoutPoint.horizontalError.derivedFromExternalWall=!this.scoutPoint.horizontalError.derivedFromExternalWall; 
    this.onValueClicked('derivedFromExternalWall')
    this.scoutPointForm.form.markAsDirty()
    this.changed.emit(this.scoutPoint)
  }
  
  onUnlevelFloorClick() {
    this.scoutPoint.verticalError.unlevelFloor=!this.scoutPoint.verticalError.unlevelFloor; 
    this.onValueClicked('unlevelFloor')
    this.scoutPointForm.form.markAsDirty()
    this.changed.emit(this.scoutPoint)
  }

  validate() {
    // ng-sortable may have lost form initial state so isDirty is not meaningful here
    return this.scoutPointForm.valid
  }


  updateScoutFromOptions() {
    let descendants : ScoutPoint[] = []
    try {
      this.addDescendants(this.scoutPoint,descendants)
    } catch ( error ) {
      this.appState.publishError(error.message,this)
    }
    this.scoutFromOptions = this.scoutPoints.filter(pt=>pt.id!=this.scoutPoint.id&&!descendants.includes(pt)) 
    //console.log('updateScoutFromOptions() = ' + this.scoutFromOptions.map(pt=>pt.name).join(','))
  }


  setScoutPoint( scoutPoint : ScoutPoint ) {
    this.scoutPoint = scoutPoint
    if ( !this.scoutPoint ) {
      return;
    }
    if ( !this.scoutPoint.envData ) {
      this.scoutPoint.envData = new ScoutPointEnvData()
    } else if (!this.scoutPoint.refData ) {
      this.scoutPoint.refData = new ScoutPointRefData()
    }
    this.updateScoutFromOptions()
  }


  private addDescendants( parentPt : ScoutPoint, descendantScouts : ScoutPoint[] ) {
    if ( !parentPt.id ) {
      const msg = 'Scout point data invalid format. Missing "id" field (check logs)'
      console.warn(msg)
      console.dir(parentPt)
      throw new Error(msg)      
    }
    this.scoutPoints.filter(pt=>pt.parentId==parentPt.id).forEach(child=>{
      descendantScouts.push(child)
      this.addDescendants( child, descendantScouts)
    })
  }

}

